import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import "../css/AddPerson.css"; // Adjust the path as necessary
import axios from "axios";
import { resolvePersonImageUrl } from "../../utils/resolvePersonImageUrl";
import { formatAccreditationCategoryLabel } from "../../utils/invitationTypeFormioUtils";


const AddPersonForm = ({ organisations = [], roles = [], relations = [] }) => {
  const { id } = useParams(); // Get personId from the URL for editing
  const navigate = useNavigate();
  const location = useLocation(); // For prefilling from search

  // State to hold form data for the Person model
  const [formData, setFormData] = useState({
    title: "",
    name: "", // First name (required)
    mname: "",
    lname: "", // Last name (required)
    preferred_name: "",
    date_of_birth: "",
    birth_city: "",
    birth_country: "",
    marital_status: "",
    deceased_date: "",
    email: "", // Email (required, unique)
    email2: "",
    password: "", // Note: password handling should ideally be done securely on the backend
    country_id: "",
    gender: "",
    job_title: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
    phone1: "",
    phone2: "",
    fax: "",
    website: "",
    facebook: "",
    twitter: "",
    instagram: "",
    other_social_media: "",
    languages: "",
    comments: "",
    web_desc: "",
    image: "", // Stored as filename, handled by multer on backend
    status: true,
    xmas1: false,
    xmas2: false,
  });

  // State for Person Roles (dynamic list)
  const [personRoles, setPersonRoles] = useState([]);
  const [newRole, setNewRole] = useState({
    organisation_id: "",
    role_id: "",
    start_date: "",
    end_date: "",
    main_role: false,
  });

  // State for Profile Image preview and upload
  const [profileImage, setProfileImage] = useState();
  const [selectedFile, setSelectedFile] = useState(null); // Actual file for upload

  // State for Person-Assistant Relationships (dynamic list)
  const [relationships, setRelationships] = useState([]);
  const [newRelationship, setNewRelationship] = useState({
    assistant_id: "",
    rel_id: "",
  });

  // Lookup data states
  const [countries, setCountries] = useState([]);
  const [orgns, setOrganisations] = useState([]); // All organisations
  const [rolenames, setRolenames] = useState([]); // All roles
  const [assistants, setAssistants] = useState([]); // All persons (for relationships)
  const [relationshipTypes, setRelationshipTypes] = useState([]); // All relationship types
  const [groups, setGroups] = useState([]);
  const [invitationTypes, setInvitationTypes] = useState([]);
  const [emsRoles, setEmsRoles] = useState([]);
  const [accreditationCategories, setAccreditationCategories] = useState([]);

  // UI related states
  const [continentCode, setContinentCode] = useState("");
  const [activeTab, setActiveTab] = useState("Profile");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(""); 
  const [competitionId, setcompetitionId] = useState(null); 

  useEffect(() => {
    if (!id) {
      if (location.state?.personData) {
        const personData = location.state.personData;
        setFormData(prev => ({
          ...prev,
          name: personData.name || '',
          mname: personData.mname || '',
          lname: personData.lname || '',
          email: personData.email || ''
        }));
      } else if (location.state?.searchValue) {
        // Case 2: Navigated from "+ Add Participant" button with a search term
        const searchValue = location.state.searchValue.trim();
        if (searchValue.includes("@")) {
          setFormData((prev) => ({ ...prev, email: searchValue }));
        } else {
          const parts = searchValue.split(" ");
          setFormData((prev) => ({
            ...prev,
            name: parts[0] || "",
            mname: parts.length > 2 ? parts.slice(1, -1).join(' ') : '', // Handle multiple middle names
            lname: parts.length > 1 ? parts[parts.length - 1] : "",
          }));
        }
      }
    }
  }, [location.state, id]); // Depend on location.state and id
  
  useEffect(() => {
  const fetchActivecompetition = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/ems/competitions?active=true`);
      const activecompetition = Array.isArray(response.data)
        ? response.data[0]
        : response.data?.competitions?.[0]; // handle both array or nested structure

      if (activecompetition?._id) {
        setcompetitionId(activecompetition._id);
      } else {
        console.warn("No active competitions found.");
        setErrorMessage("No active competition found. A person cannot be added as a participant.");
      }
    } catch (error) {
      console.error("Error fetching active competition:", error);
      setErrorMessage("Failed to fetch active competition. Cannot save person to an competition.");
    }
  };

  if (!id) fetchActivecompetition();
}, [id]);

  // --- Handle form input changes ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Update continent code when country changes
    if (name === "country_id") {
      const selectedCountry = countries.find((country) => country._id === value);
      setContinentCode(selectedCountry ? selectedCountry.continent : "");
    }
  };

  // --- Fetch data for dropdowns and existing person data (if editing) ---
  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        // Fetch specific person data if 'id' exists (editing mode)
        if (id) {
          const { data } = await axios.get(
            `${process.env.REACT_APP_API_URL}/persons/${id}`
          );
          setFormData(data.person);
          setProfileImage(
            resolvePersonImageUrl(data.person.image, process.env.REACT_APP_API_URL || "") ||
              "/images/avatar.png"
          );
          setPersonRoles(data.roles);
          setRelationships(data.relationships);
        } else {
          // If adding a new person, ensure image is default
          setProfileImage("/images/avatar.png");
        }

        // Fetch common lookup data (organisations, roles, assistants, relationship types)
        const [orgRes, roleRes, assistantRes, relTypeRes, countryRes, groupRes, invitationTypeRes, emsRoleRes, accreditationCategoryRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/organisations`),
          axios.get(`${process.env.REACT_APP_API_URL}/roles`),
          axios.get(`${process.env.REACT_APP_API_URL}/persons`), // All persons for assistant dropdown
          axios.get(`${process.env.REACT_APP_API_URL}/relationship-types`),
          axios.get(`${process.env.REACT_APP_API_URL}/countries`), // Fetch countries here too
          axios.get(`${process.env.REACT_APP_API_URL}/ems/groups`),
          axios.get(`${process.env.REACT_APP_API_URL}/ems/invitation-types`),
          axios.get(`${process.env.REACT_APP_API_URL}/ems/roles`),
          axios.get(`${process.env.REACT_APP_API_URL}/ems/accreditation-categories`),
        ]);

        setOrganisations(orgRes.data.map(({ _id, name }) => ({ _id, name })));
        setRolenames(roleRes.data.map(({ _id, role_name }) => ({ _id, role_name })));
        // Filter out the current person from the assistants list for self-relations
        setAssistants(assistantRes.data.filter((assistant) => assistant._id !== id));
        setRelationshipTypes(relTypeRes.data);
        setCountries(countryRes.data);
        setGroups(groupRes.data.map(({ _id, group_name }) => ({ _id, group_name })));
        setInvitationTypes(Array.isArray(invitationTypeRes.data) ? invitationTypeRes.data : []);
        setEmsRoles(Array.isArray(emsRoleRes.data) ? emsRoleRes.data : []);
        setAccreditationCategories(Array.isArray(accreditationCategoryRes.data) ? accreditationCategoryRes.data : []);
        // Set continent code if country_id is already in formData (from fetched data or prefill)
        if (formData.country_id) {
          const selectedCountry = countryRes.data.find(
            (country) => country._id === formData.country_id
          );
          if (selectedCountry) {
            setContinentCode(selectedCountry.continent);
          }
        }

      } catch (error) {
        console.error("Error fetching initial data:", error);
        setErrorMessage("Failed to load necessary data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, formData.country_id]); // Added formData.country_id to dependency array for continent update


  // --- Role Management Functions ---
  const handleRoleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewRole((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddRole = () => {
    if (!newRole.role_id || !newRole.organisation_id) {
      setErrorMessage("Please select both organization and role to add a new role.");
      return;
    }
    setErrorMessage(""); // Clear previous error
    setPersonRoles((prev) => [...prev, { ...newRole, _id: Date.now().toString() }]); // Use _id for consistency
    setNewRole({
      organisation_id: "",
      role_id: "",
      start_date: "",
      end_date: "",
      main_role: false,
    });
  };

  const handleRemoveRole = (roleIdToRemove) => {
    setPersonRoles((prev) => prev.filter((role) => role._id !== roleIdToRemove));
  };

  // --- Image Upload ---
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file); // Store the actual file for submission
    const reader = new FileReader();
    reader.onload = () => setProfileImage(reader.result); // For preview
    reader.readAsDataURL(file);
  };

  // --- Relationship Management Functions ---
  const handleRelationshipInputChange = (e) => {
    const { name, value } = e.target;
    setNewRelationship((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddRelationship = () => {
    if (!newRelationship.assistant_id || !newRelationship.rel_id) {
      setErrorMessage("Please select both a person and a relationship type to add a new relationship.");
      return;
    }
    setErrorMessage(""); // Clear previous error
    setRelationships((prev) => [
      ...prev,
      { ...newRelationship, _id: Date.now().toString() }, // Use _id for consistency
    ]);
    setNewRelationship({
      assistant_id: "",
      rel_id: "",
    });
  };

  const handleRemoveRelationship = (relIdToRemove) => {
    setRelationships((prev) => prev.filter((rel) => rel._id !== relIdToRemove));
  };


  const handleSubmit = async (e) => {
  e.preventDefault();
  setErrorMessage("");

  try {
    // ✅ Validation
    if (!formData.name || !formData.lname || !formData.email) {
      setErrorMessage("Please fill First Name, Last Name, and Email.");
      return;
    }
    if (!competitionId) {
      setErrorMessage("No active competition found. Cannot add participant.");
      return;
    }

    // ✅ Prepare payloads
    // ✅ Prepare payloads
const personData = { ...formData };

    const participantData = {
      competition_id: competitionId,
      group_id: formData.group_id && formData.group_id !== "" ? formData.group_id : null,
      invitation_type_id: formData.invitation_type_id || null,
      role_id: formData.participant_role_id || null,
      accreditation_category_id: formData.accreditation_category_id || null,
    };

    const combinedPayload = {
      personData,
      participantData,
      group_id: formData.group_id && formData.group_id !== "" ? formData.group_id : null,
      roles: personRoles,
      relationships,
    };


    let response;

    if (id) {
      const formToUpdate = new FormData();
      Object.entries(formData).forEach(([key, value]) =>
        formToUpdate.append(key, value ?? "")
      );
      formToUpdate.append("roles", JSON.stringify(personRoles));
      formToUpdate.append("relationships", JSON.stringify(relationships));
      if (selectedFile) formToUpdate.append("image", selectedFile);

      response = await axios.put(
        `${process.env.REACT_APP_API_URL}/persons/${id}`,
        formToUpdate,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      alert(response.data.message || "Person updated successfully!");
    } else {
      // 🟢 Create new person + participant
      response = await axios.post(
        `${process.env.REACT_APP_API_URL}/persons/create-with-participant`,
        combinedPayload,
        { headers: { "Content-Type": "application/json" } }
      );
      alert(response.data.message || "Person and Participant created successfully!");
    }

    navigate("/ems/participants");
  } catch (error) {
    console.error("Error saving data:", error.response?.data || error);
    setErrorMessage(
      error.response?.data?.message || "Failed to save data. Please try again."
    );
  }
};



  const handleEditorChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      web_desc: content,
    }));
  };

  const renderProfileTab = () => (
    <div>
      <div className="row mt-3">
        <div className="form-group col-md-2">
          <label>Title</label>
          <select
            className="form-control py-3"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
          >
            <option value="">Title</option>
            <option value="Mr.">Mr.</option>
            <option value="Mrs.">Mrs.</option>
            <option value="Ms.">Ms.</option>
            <option value="Dr.">Dr.</option>
            <option value="Prof.">Prof.</option>
          </select>
        </div>
        <div className="form-group col-md-2">
          <label>Gender</label>
          <select
            className="form-control py-3"
            name="gender"
            value={formData.gender}
            onChange={handleInputChange}
          >
            <option value="">Select</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
         <div className="form-group col-md-2">
          <label>Job Title</label>
          <input
            type="text"
            className="form-control py-3"
            name="job_title"
            value={formData.job_title}
            onChange={handleInputChange}
            required // Made required
          />
        </div>
      </div>
      
      <div className="row mt-3">
        <div className="form-group col-md-2">
          <label>First Name</label>
          <input
            type="text"
            className="form-control py-3"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required // Made required
          />
        </div>
        <div className="form-group col-md-2">
          <label>Middle Name</label>
          <input
            type="text"
            className="form-control py-3"
            name="mname"
            value={formData.mname}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-2">
          <label>Last Name</label>
          <input
            type="text"
            className="form-control py-3"
            name="lname"
            value={formData.lname}
            onChange={handleInputChange}
            required // Made required
          />
        </div>
        <div className="form-group col-md-2">
          <label>Preferred Name</label>
          <input
            type="text"
            className="form-control py-3"
            name="preferred_name"
            value={formData.preferred_name}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-2">
          <label>Email 1</label>
          <input
            type="email"
            className="form-control py-3"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required // Made required
          />
        </div>
        <div className="form-group col-md-2">
        <label>Groups</label>
        <select
          className="form-control py-3"
          name="group_id"
          value={formData.group_id || ""}
          onChange={handleInputChange}
          required
        >
          <option value="">Select Group</option>
          {groups.map((group) => (
            <option key={group._id} value={group._id}>
              {group.group_name}
            </option>
          ))}
        </select>
      </div>
      </div>

      <div className="row mt-3">
        <div className="form-group col-md-3">
          <label>Role</label>
          <select
            className="form-control py-3"
            name="participant_role_id"
            value={formData.participant_role_id || ""}
            onChange={handleInputChange}
          >
            <option value="">Select Role</option>
            {emsRoles.map((role) => (
              <option key={role._id} value={role._id}>
                {role.role_name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group col-md-3">
          <label>Invitation Type</label>
          <select
            className="form-control py-3"
            name="invitation_type_id"
            value={formData.invitation_type_id || ""}
            onChange={handleInputChange}
          >
            <option value="">Select Invitation Type</option>
            {invitationTypes.map((type) => (
              <option key={type._id} value={type._id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group col-md-3">
          <label>Accreditation Category</label>
          <select
            className="form-control py-3"
            name="accreditation_category_id"
            value={formData.accreditation_category_id || ""}
            onChange={handleInputChange}
          >
            <option value="">Select Accreditation Category</option>
            {accreditationCategories.map((category) => (
              <option key={category._id} value={category._id}>
                {formatAccreditationCategoryLabel(category)}
              </option>
            ))}
          </select>
        </div>

      </div>
    </div>
  );

  const renderAddressTab = () => (
    <div>
      <h4>Address Details</h4>
      <div className="row mt-3">
        <div className="form-group col-md-3">
          <label>Landline</label>
          <input
            type="text"
            className="form-control py-3"
            name="phone1"
            value={formData.phone1}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-3">
          <label>Mobile Phone</label>
          <input
            type="text"
            className="form-control py-3"
            name="phone2"
            value={formData.phone2}
            onChange={handleInputChange}
          />
        </div>
        
        <div className="form-group col-md-3">
          <label>Email 2</label>
          <input
            type="email"
            className="form-control py-3"
            name="email2"
            value={formData.email2}
            onChange={handleInputChange}
          />
        </div>
      </div>
      <div className="row mt-3">
        <div className="form-group col-md-3">
          <label>Address Line 1</label>
          <input
            type="text"
            className="form-control py-3"
            name="address1"
            value={formData.address1}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-3">
          <label>Address Line 2</label>
          <input
            type="text"
            className="form-control py-3"
            name="address2"
            value={formData.address2}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-3">
          <label>Fax</label>
          <input
            type="text"
            className="form-control py-3"
            name="fax"
            value={formData.fax}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-3">
          <label>Continent</label>
          <input
            type="text"
            className="form-control py-3"
            value={continentCode}
            readOnly
            style={{ color: "gray", backgroundColor: "#f9f9f9" }}
          />
        </div>
      </div>
      <div className="row mt-3">
        <div className="form-group col-md-3">
          <label>Zip Code</label>
          <input
            type="text"
            className="form-control py-3"
            name="zipcode"
            value={formData.zipcode}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-3">
          <label>City</label>
          <input
            type="text"
            className="form-control py-3"
            name="city"
            value={formData.city}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-3">
          <label>State</label>
          <input
            type="text"
            className="form-control py-3"
            name="state"
            value={formData.state}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-3">
          <label>Country</label>
          <select
            className="form-control py-3"
            name="country_id"
            value={formData.country_id}
            onChange={handleInputChange}
          >
            <option value="">Select Country</option>
            {countries.map((country) => (
              <option key={country._id} value={country._1d || country._id}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  const renderPersonalTab = () => (
    <div>
      <h4>Personal Details</h4>
      <div className="row mt-3">
        <div className="form-group col-md-3">
          <label>Date of Birth</label>
          <input
            type="date"
            className="form-control py-3"
            name="date_of_birth"
            value={formData.date_of_birth}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group col-md-3">
          <label>Deseased Date</label>
          <input
            type="date"
            className="form-control py-3"
            name="deceased_date"
            value={formData.deceased_date}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-3">
          <label>City of Birth</label>
          <input
            type="text"
            className="form-control py-3"
            name="birth_city"
            value={formData.birth_city}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-3">
          <label>Country of Birth</label>
          <select
            className="form-control py-3"
            name="birth_country"
            value={formData.birth_country}
            onChange={handleInputChange}
          >
            <option value="">Select Country</option>
            {countries.map((country) => (
              <option key={country._id} value={country._id}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="row mt-3">
        <div className="form-group col-md-3">
          <label>Marital Status</label>
          <select
            className="form-control py-3"
            name="marital_status"
            value={formData.marital_status}
            onChange={handleInputChange}
          >
            <option value="">Marital Status</option>
            <option value="Single">Single</option>
            <option value="Married">Married</option>
            <option value="Widowed">Widowed</option>
            <option value="Separated">Separated</option>
            <option value="Divorced">Divorced</option>
          </select>
        </div>
        <div className="form-group col-md-3">
          <label>Website</label>
          <input
            type="text"
            className="form-control py-3"
            name="website"
            value={formData.website}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-3">
          <label>Facebook</label>
          <input
            type="text"
            className="form-control py-3"
            name="facebook"
            value={formData.facebook}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-3">
          <label>Twitter</label>
          <input
            type="text"
            className="form-control py-3"
            name="twitter"
            value={formData.twitter}
            onChange={handleInputChange}
          />
        </div>
      </div>
      <div className="row mt-3">
        <div className="form-group col-md-3">
          <label>Instagram</label>
          <input
            type="text"
            className="form-control py-3"
            name="instagram"
            value={formData.instagram}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-3">
          <label>Other Social Media</label>
          <input
            type="text"
            className="form-control py-3"
            name="other_social_media"
            value={formData.other_social_media}
            onChange={handleInputChange}
          />
        </div>
      </div>
    </div>
  );

  const renderOtherTab = () => (
    <div>
      <h4>Other Details</h4>
      <div className="row mt-3">
        <div className="form-group col-md-3">
          <label>Languages</label>
          <textarea
            className="form-control py-3"
            name="languages"
            rows={10}
            value={formData.languages}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-3">
          <label>Comments</label>
          <textarea
            className="form-control py-3"
            name="comments"
            value={formData.comments}
            rows={10}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group col-md-6">
          <label>Web Description</label>
          <Editor
            apiKey="zk2zzdzsc5ofia3cqt9boi8numoj5fr2y4lj21p6lstv9t9t"
            value={formData.web_desc}
            init={{
              height: 246,
              menubar: false,
              plugins: [
                "advlist autolink lists link image charmap print preview anchor",
                "searchreplace visualblocks code fullscreen",
                "insertdatetime media table paste code help wordcount",
              ],
              toolbar:
                "undo redo | formatselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help",
            }}
            onEditorChange={handleEditorChange}
          />
        </div>

        <div className="col-12 mt-4">
          <div>
            <h6 style={{ marginBottom: "14px" }}>Xmas Cards</h6>
            <div className="add-person form-group">
              <div className="add-person form-check col-12 col-md-6">
              <input
                type="checkbox"
                className="form-check-input py-3"
                id="xmas1"
                name="xmas1"
                checked={formData.xmas1}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    [e.target.name]: e.target.checked,
                  }))
                }
              />
              <label
                className="add-person form-check-label"
                htmlFor="xmas1"
                style={{ textAlign: "left" }}
              >
                Xmas Card
              </label>
              </div>
            </div>
            <div
              className="add-person form-check col-12 col-md-6 mt-3"
              style={{ paddingLeft: 0 }}
            >
            <input
              type="checkbox"
              className="form-check-input py-3"
              id="xmas2"
              name="xmas2"
              checked={formData.xmas2}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  [e.target.name]: e.target.checked,
                }))
              }
            />
            <label className="add-person form-check-label" htmlFor="xmas2">
              CEO Xmas Card
            </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRolesTab = () => (
    <div>
      <h4>Roles</h4>
      <table className="table">
        <thead>
          <tr>
            <th className="bg-warning text-white">Organization</th>
            <th className="bg-warning text-white">Role</th>
            <th className="bg-warning text-white">Start Date</th>
            <th className="bg-warning text-white">End Date</th>
            <th className="bg-warning text-white">Main Role</th>
            <th className="bg-warning text-white">Actions</th>
          </tr>
        </thead>
        <tbody>
          {personRoles.map((role) => (
            <tr key={role._id}>
              <td>
                {orgns.find((org) => org._id === role.organisation_id) ? (
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() =>
                      navigate(`/organisation/${role.organisation_id}`, {
                        state: {
                          organisation: orgns.find(
                            (org) => org._id === role.organisation_id
                          ),
                        },
                      })
                    }
                  >
                    {
                      orgns.find((org) => org._id === role.organisation_id)
                        ?.name
                    }
                  </button>
                ) : (
                  "N/A"
                )}
              </td>
              <td>
                {rolenames.find((r) => r._id === role.role_id)?.role_name ||
                  "N/A"}
              </td>
              <td>{role.start_date || "N/A"}</td>
              <td>{role.end_date || "N/A"}</td>
              <td>{role.main_role ? "Yes" : "No"}</td>
              <td>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => handleRemoveRole(role._id)}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <h5>Add New Role</h5>
      <div className="row m-0">
        <div className="col-md-12 py-3 bg-white">
          <div className="row my-3">
            <div className="form-group col-md-3">
              <label>Organization</label>
              <select
                className="form-control py-3"
                name="organisation_id"
                value={newRole.organisation_id}
                onChange={handleRoleInputChange}
              >
                <option value="">Select Organization</option>
                {orgns.map((org) => (
                  <option key={org._id} value={org._id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group col-md-2">
              <label>Role</label>
              <select
                className="form-control py-3"
                name="role_id"
                value={newRole.role_id}
                onChange={handleRoleInputChange}
              >
                <option value="">Select Role</option>
                {rolenames.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.role_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group col-md-2">
              <label>Start Date</label>
              <input
                type="date"
                className="form-control py-3"
                name="start_date"
                value={newRole.start_date}
                onChange={handleRoleInputChange}
              />
            </div>
            <div className="form-group col-md-2">
              <label>End Date</label>
              <input
                type="date"
                className="form-control py-3"
                name="end_date"
                value={newRole.end_date}
                onChange={handleRoleInputChange}
              />
            </div>
            <div className="form-group col-md-3">
              <input
                type="checkbox"
                className="form-check-input me-2 mt-4"
                id="mainRole"
                name="main_role"
                checked={newRole.main_role}
                onChange={handleRoleInputChange}
              />
              <label className="form-check-label mt-4" htmlFor="mainRole">
                Main Role
              </label>
              <button
                type="button"
                className="btn btn-success mt-3 float-end"
                onClick={handleAddRole}
              >
                Add Role
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRelationshipsTab = () => (
    <div>
      <h4>Relationships</h4>
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Relation</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {relationships.map((rel) => (
            <tr key={rel._id}>
              <td>
                {assistants.find((a) => a._id === rel.assistant_id)?.name ||
                  "N/A"}
              </td>
              <td>
                {relationshipTypes.find((rt) => rt._id === rel.rel_id)
                  ?.rel_name || "N/A"}
              </td>
              <td>
                {assistants.find((a) => a._id === rel.assistant_id)?.email ||
                  "N/A"}
              </td>
              <td>
                {assistants.find((a) => a._id === rel.assistant_id)?.phone2 ||
                  assistants.find((a) => a._id === rel.assistant_id)?.phone1 ||
                  "N/A"}
              </td>
              <td>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => handleRemoveRelationship(rel._id)}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h5>Add New Relationship</h5>
      <div className="row m-0">
        <div className="col-md-12 py-3 bg-white">
          <div className="row my-3">
            <div className="form-group col-md-4">
              <label>Person</label>
              <select
                className="form-control py-3"
                name="assistant_id"
                value={newRelationship.assistant_id}
                onChange={handleRelationshipInputChange}
              >
                <option value="">Select Assistant</option>
                {assistants.map((assistant) => (
                  <option key={assistant._id} value={assistant._id}>
                    {assistant.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group col-md-4">
              <label>Relationship Type</label>
              <select
                className="form-control py-3"
                name="rel_id"
                value={newRelationship.rel_id}
                onChange={handleRelationshipInputChange}
              >
                <option value="">Select Relationship Type</option>
                {relationshipTypes.map((relType) => (
                  <option key={relType._id} value={relType._id}>
                    {relType.rel_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group col-md-4">
              <button
                type="button"
                className="btn btn-success mt-3 float-end"
                onClick={handleAddRelationship}
              >
                Add Relationship
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return loading ? (
    <div>Loading...</div>
  ) : (
    <div className="mt-4 addperson-page">
      <div className="row mb-4">
        <div className="col-md-2 col-sm-3">
          <img
            src={profileImage}
            alt="Profile"
            className="rounded-circle"
            width="120"
          />
          <div>
            <button
              type="button"
              className="btn btn-secondary mt-2"
              onClick={() => document.getElementById("profileImageInput").click()}
            >
              Upload Profile Photo
            </button>
            <input
              type="file"
              id="profileImageInput"
              style={{ display: "none" }}
              accept="image/*"
              onChange={handleImageUpload}
            />
          </div>
        </div>
        <div className="col-sm-9 col-md-10 d-flex align-self-center justify-content-center">
          {" "}
          <h1>
            {formData.name || formData.mname || formData.lname
              ? `${formData.name || ""} ${formData.mname || ""} ${
                  formData.lname || ""
                }`.trim()
              : "Add/Edit Person"}
          </h1>
        </div>
      </div>
      <div className="row border border-white p-3 rounded">
        <div className="tabs">
          {["Profile", "Address", "Personal", "Other", "Roles", "Relationships"].map(
            (tab) => (
              <button
                key={tab}
                className={`btn px-5 py-3 mx-2 ${
                  activeTab === tab ? "btn-warning" : "btn-success"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            )
          )}
        </div>
        {errorMessage && (
          <div className="alert alert-danger mt-3" role="alert">
            {errorMessage}
          </div>
        )}
        <form onSubmit={handleSubmit} className="mt-4">
          {activeTab === "Profile" && renderProfileTab()}
          {activeTab === "Address" && renderAddressTab()}
          {activeTab === "Personal" && renderPersonalTab()}
          {activeTab === "Other" && renderOtherTab()}
          {activeTab === "Roles" && renderRolesTab()}
          {activeTab === "Relationships" && renderRelationshipsTab()}
          <div className="row mt-4">
            <div className="col-12 text-center">
              <button type="submit" className="btn px-3 mx-2" style={{backgroundColor: '#D9A128'}} >
                Add to Current Tournament & Save
              </button>
              <Link to="/" className="btn btn-secondary px-5">
                Cancel
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPersonForm;